import type { UserActivityDataPoint } from '@/lib/api/user-activity';
import type { ActivityInterval } from '@/lib/api/user-activity';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface ActivityTableProps {
  data: UserActivityDataPoint[];
  interval: ActivityInterval;
}

function formatDate(isoDate: string, interval: ActivityInterval): string {
  const d = new Date(isoDate);
  if (interval === 'day') {
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
  if (interval === 'week') {
    const end = new Date(d);
    end.setDate(end.getDate() + 6);
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatShortDuration(minutes: number): string {
  if (minutes === 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const metricLabels = [
  { key: 'epicRequestsCreated' as const, label: 'ER', dot: 'bg-cyan-500' },
  { key: 'epicsCreated' as const, label: 'Epics', dot: 'bg-indigo-500' },
  { key: 'totalElapsedMinutes' as const, label: 'Elapsed', dot: 'bg-rose-500', isTime: true },
];

export function ActivityTable({ data, interval }: ActivityTableProps) {
  const isMobile = useIsMobile();
  const sorted = [...data];

  if (sorted.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No activity recorded yet
      </div>
    );
  }

  // Mobile: card-based layout
  if (isMobile) {
    return (
      <div className="space-y-2">
        {sorted.map((point, idx) => {
          const hasActivity = point.totalActivity > 0;
          return (
            <div
              key={idx}
              className="rounded-lg bg-white/[0.02] p-3 space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">
                  {formatDate(point.intervalStart, interval)}
                </span>
                <span className={cn(
                  "text-xs tabular-nums",
                  hasActivity ? "text-foreground font-medium" : "text-muted-foreground"
                )}>
                  {point.totalActivity} total
                </span>
              </div>
              <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                {metricLabels.map((m) => {
                  const val = point[m.key];
                  const display = m.isTime ? formatShortDuration(val) : (val || '—');
                  return (
                    <div key={m.key} className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                      <span className="text-xs text-muted-foreground">{m.label}</span>
                      <span className={cn(
                        "text-xs tabular-nums ml-auto",
                        val > 0 ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {display}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop: tabular row layout (unchanged)
  return (
    <div className="space-y-1">
      {sorted.map((point, idx) => {
        const hasActivity = point.totalActivity > 0;
        return (
          <div
            key={idx}
            className="flex items-center py-2.5 px-3 rounded-lg hover:bg-white/[0.03] transition-colors"
          >
            <span className="text-sm text-foreground min-w-0 truncate flex-1">
              {formatDate(point.intervalStart, interval)}
            </span>
            {metricLabels.map((m) => {
              const val = point[m.key];
              const display = m.isTime ? formatShortDuration(val) : (val || '—');
              return (
                <span key={m.key} className={cn("w-12 text-right text-xs tabular-nums", hasActivity ? "text-foreground" : "text-muted-foreground")}>
                  {display}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
